import * as MenuPrimitive from '@radix-ui/react-dropdown-menu';
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router';
import { Icon } from './icon';

// A row of a section's pages (TF-215): Settings, the CRM's sections. From 600 px it shows as many as
// fit, measured, and the rest go into a "…" menu at its end, so none is cut off beyond the edge
// with nothing to say it is there; the page you are on always stays in the row. On a phone the row
// scrolls sideways under a finger, with a fade at the edge that has more.

export interface SectionLink {
  to: string;
  label: string;
}

/** Below this width a finger scrolls the row; from it, the row fits and the rest goes in "…". */
const COMPACT = '(max-width: 599px)';

/** Which links show in the row: as many as fit beside the "…" button, the current one always among them. */
export function fittingLinks(widths: number[], gap: number, available: number, more: number, current: number): number[] {
  const all = widths.map((_, index) => index);
  const total = widths.reduce((sum, width) => sum + width, 0) + gap * Math.max(0, widths.length - 1);
  if (total <= available) return all;
  const shown: number[] = [];
  let used = more;
  for (const index of all) {
    if (used + widths[index]! + gap > available) break;
    shown.push(index);
    used += widths[index]! + gap;
  }
  // The current page takes the last places it needs, the ones it displaces go into the menu.
  if (current >= 0 && !shown.includes(current)) {
    while (shown.length > 0 && shown.reduce((sum, index) => sum + widths[index]! + gap, more) + widths[current]! + gap > available) shown.pop();
    shown.push(current);
  }
  return shown.length > 0 ? shown : [Math.max(0, current)];
}

export function SectionNav({ label, moreLabel, links }: { label: string; moreLabel: string; links: SectionLink[] }) {
  const { pathname } = useLocation();
  const row = useRef<HTMLElement>(null);
  const ruler = useRef<HTMLDivElement>(null);
  const [compact, setCompact] = useState(() => typeof window !== 'undefined' && window.matchMedia(COMPACT).matches);
  const [shown, setShown] = useState<number[] | null>(null);
  const [fade, setFade] = useState('');
  const current = links.findIndex((link) => pathname === link.to || pathname.startsWith(`${link.to}/`));
  const key = links.map((link) => link.label).join('|');

  useEffect(() => {
    const media = window.matchMedia(COMPACT);
    const change = () => setCompact(media.matches);
    media.addEventListener('change', change);
    return () => media.removeEventListener('change', change);
  }, []);

  // From 600 px: measured against the row's width, the labels' own widths (language, text size).
  useLayoutEffect(() => {
    const nav = row.current;
    const measured = ruler.current;
    if (compact || !nav || !measured) return;
    const update = () => {
      const widths = [...measured.querySelectorAll<HTMLElement>('[data-measure="link"]')].map((element) => element.getBoundingClientRect().width);
      const more = measured.querySelector<HTMLElement>('[data-measure="more"]')!.getBoundingClientRect().width;
      const gap = Number.parseFloat(getComputedStyle(nav).columnGap) || 0;
      const next = fittingLinks(widths, gap, nav.clientWidth, more + gap, current);
      setShown((before) => (before && before.join() === next.join() ? before : next));
    };
    update();
    const observer = new ResizeObserver(update);
    observer.observe(nav);
    observer.observe(measured);
    return () => observer.disconnect();
  }, [compact, key, current]);

  // On a phone: the current page scrolled into the row (the row only), and a fade where it has more.
  useEffect(() => {
    const nav = row.current;
    if (!compact || !nav) return;
    const active = nav.querySelector<HTMLElement>('.active');
    if (active && (active.offsetLeft < nav.scrollLeft || active.offsetLeft + active.offsetWidth > nav.scrollLeft + nav.clientWidth)) {
      nav.scrollLeft = active.offsetLeft - (nav.clientWidth - active.offsetWidth) / 2;
    }
    const edges = () => {
      const more = [nav.scrollLeft > 1 && 'start', nav.scrollLeft + nav.clientWidth < nav.scrollWidth - 1 && 'end'].filter(Boolean).join(' ');
      setFade(more);
    };
    edges();
    nav.addEventListener('scroll', edges, { passive: true });
    const observer = new ResizeObserver(edges);
    observer.observe(nav);
    return () => {
      nav.removeEventListener('scroll', edges);
      observer.disconnect();
    };
  }, [compact, pathname, key]);

  // The links may have just become fewer (whose they are became known): until measured again, only those still there.
  const inRow = compact || !shown ? links.map((_, index) => index) : shown.filter((index) => index < links.length);
  const inMenu = compact || !shown ? [] : links.map((_, index) => index).filter((index) => !shown.includes(index));
  return (
    <>
      <nav ref={row} className="subnav" aria-label={label} data-fade={compact ? fade : undefined}>
        {inRow.map((index) => (
          <NavLink key={links[index]!.to} to={links[index]!.to} className={({ isActive }) => (isActive ? 'subnav-link active' : 'subnav-link')}>
            {links[index]!.label}
          </NavLink>
        ))}
        {inMenu.length > 0 && (
          <MenuPrimitive.Root>
            <MenuPrimitive.Trigger className="subnav-link subnav-more" aria-label={`${moreLabel} (${inMenu.length})`}>
              <Icon name="more_horiz" size={20} />
              <span className="subnav-more-count" aria-hidden>
                {inMenu.length}
              </span>
            </MenuPrimitive.Trigger>
            <MenuPrimitive.Portal>
              <MenuPrimitive.Content className="menu" align="end" sideOffset={4}>
                {inMenu.map((index) => (
                  <MenuPrimitive.Item key={links[index]!.to} asChild className="menu-item">
                    <Link to={links[index]!.to} aria-current={index === current ? 'page' : undefined}>
                      {index === current ? <Icon name="check" /> : <span className="icon-space" />}
                      {links[index]!.label}
                    </Link>
                  </MenuPrimitive.Item>
                ))}
              </MenuPrimitive.Content>
            </MenuPrimitive.Portal>
          </MenuPrimitive.Root>
        )}
      </nav>
      {/* The labels as the row draws them, measured out of sight (nothing here is read or reached). */}
      {!compact && (
        <div ref={ruler} className="subnav-ruler" aria-hidden>
          {links.map((link) => (
            <span key={link.to} className="subnav-link" data-measure="link">
              {link.label}
            </span>
          ))}
          <span className="subnav-link subnav-more" data-measure="more">
            <Icon name="more_horiz" size={20} />
            <span className="subnav-more-count">{links.length}</span>
          </span>
        </div>
      )}
    </>
  );
}
